import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Modal, Form, Input, Button, Select, Card, Row, Col, message, Tooltip, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined, DownOutlined, RightOutlined, ContactsOutlined, InfoCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../../utils/hooks/redux';
import { fetchEstablishmentType } from '../../../redux/features/establishmentType/establishmentAsyncThunk';
import { fetchSingleEstablishmentLocation } from '../../../redux/features/establishmentLocation/establishmentLocationAsyncThunk';
import { EstablishmentLocation, EstablishmentType, Contact, Report } from '../../../types';
import PlacesContactsDrawer from '../../EstablishmentType/PlacesContactsDrawer';
import api from '../../../utils/constants/api';
import { apiEndpoints } from '../../../utils/constants/apiEndpoints';
import { ApiResponse } from '../../../types/APIResponse';

interface LFNotifiedModalProps {
  isOpen: boolean;
  onClose: () => void;
  // sendEmail reflects the "Notify customer via email" footer checkbox (defaults to checked
  // → sendEmail = true). Consumers pass it through to updateLostItemStatus so the LF Notified
  // email respects the admin's choice.
  onSave: (values: LFDepartmentDetails[], sendEmail: boolean) => void;
  loading: boolean;
  report: Report | null;
  confirmButtonText?: string;
  existingNotifications?: any[];
  skipAutoPopulate?: boolean;
}

export interface LFDepartmentDetails {
  lfDepartmentName: string;
  lfContactPerson: string;
  lfContactEmail: string;
  lfContactPhone: string;
  lfNotes?: string;
  establishmentTypeId?: string;
  establishmentLocationId?: string;
  specificType?: string;
  contactId?: string;
  isNewContact?: boolean;
  additionalComments?: string;
  establishmentName?: string;
  establishmentCode?: string;
  adminComment?: string;
}

interface LFNotifyEntry {
  key: string;
  establishmentTypeId?: string;
  specificType?: string;
  establishmentLocationId?: string;
  contactId?: string;
  additionalComments?: string;
  _isExisting?: boolean;
}

const MAX_ENTRIES = 10;

const LFNotifiedModal: React.FC<LFNotifiedModalProps> = ({
  isOpen,
  onClose,
  onSave,
  loading,
  report,
  confirmButtonText,
  existingNotifications,
  skipAutoPopulate
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { data: establishmentTypes } = useAppSelector((state) => state.establishmentType);

  const [entries, setEntries] = useState<LFNotifyEntry[]>([{ key: '1' }]);
  const [expandedEntryKey, setExpandedEntryKey] = useState<string>('1');
  // Defaults ON so the customer is notified by default — admins must uncheck to skip the
  // email. Positive framing avoids the double-negative misread risk of a "Do not notify" flag.
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(true);
  const [drawerEntryKey, setDrawerEntryKey] = useState<string | null>(null);
  const [establishmentDrawerVisible, setEstablishmentDrawerVisible] = useState<boolean>(false);
  const [establishmentForDrawer, setEstablishmentForDrawer] = useState<EstablishmentLocation | null>(null);

  // Local cache: establishmentTypeId -> EstablishmentLocation[]
  const [locationsCache, setLocationsCache] = useState<Record<string, EstablishmentLocation[]>>({});
  const [loadingLocations, setLoadingLocations] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen && establishmentTypes.length === 0) {
      dispatch(fetchEstablishmentType());
    }
  }, [isOpen, dispatch, establishmentTypes.length]);

  // Fetch locations for a given establishment type ID (with cache)
  const fetchLocationsForType = useCallback(async (establishmentTypeId: string) => {
    if (locationsCache[establishmentTypeId] || loadingLocations[establishmentTypeId]) return;

    setLoadingLocations(prev => ({ ...prev, [establishmentTypeId]: true }));
    try {
      const response = await api.get<ApiResponse<EstablishmentLocation[]>>(
        `${apiEndpoints.establishmentLocation.getAll}?establishmentTypeId=${establishmentTypeId}`
      );
      setLocationsCache(prev => ({ ...prev, [establishmentTypeId]: response.data || [] }));
    } catch (error) {
      console.error('Failed to fetch locations for type:', establishmentTypeId, error);
      setLocationsCache(prev => ({ ...prev, [establishmentTypeId]: [] }));
    } finally {
      setLoadingLocations(prev => ({ ...prev, [establishmentTypeId]: false }));
    }
  }, [locationsCache, loadingLocations]);

  // Get specific types for an establishment type
  const getSpecificTypes = useCallback((establishmentTypeId?: string): string[] => {
    if (!establishmentTypeId) return [];
    const estType = establishmentTypes.find((t: EstablishmentType) => t.id === establishmentTypeId);
    if (!estType?.specificTypes) return [];
    return estType.specificTypes.map((st: any) => st.name);
  }, [establishmentTypes]);

  // Get filtered locations for an entry
  const getLocationsForEntry = useCallback((entry: LFNotifyEntry): EstablishmentLocation[] => {
    if (!entry.establishmentTypeId) return [];
    let locations = locationsCache[entry.establishmentTypeId] || [];
    if (entry.specificType) {
      locations = locations.filter((l: EstablishmentLocation) => l.specificType === entry.specificType);
    }
    return locations.sort((a, b) => a.name.localeCompare(b.name));
  }, [locationsCache]);

  // Get contacts for a specific location within an entry's context
  const getContactsForLocation = useCallback((entry: LFNotifyEntry) => {
    if (!entry.establishmentLocationId || !entry.establishmentTypeId) {
      return { establishmentContacts: [] as Contact[], placeContacts: [] as (Contact & { placeName?: string })[] };
    }
    const locations = locationsCache[entry.establishmentTypeId] || [];
    const location = locations.find((l: EstablishmentLocation) => l.id === entry.establishmentLocationId);
    if (!location) return { establishmentContacts: [] as Contact[], placeContacts: [] as (Contact & { placeName?: string })[] };

    const contactIds = new Set<string>();
    const establishmentContacts: Contact[] = [];
    const placeContacts: Array<Contact & { placeName?: string }> = [];

    // Honor the address-book ordering: contacts are reorderable via drag-and-drop
    // and the order is persisted on each contact's `index` field. Sort here so
    // the dropdown matches what the admin sees in the address book.
    const byIndex = (a: any, b: any) => (a?.index ?? 0) - (b?.index ?? 0);

    if (location.contacts && Array.isArray(location.contacts)) {
      [...location.contacts].sort(byIndex).forEach((contact: Contact) => {
        if (!contactIds.has(contact.id)) {
          contactIds.add(contact.id);
          establishmentContacts.push(contact);
        }
      });
    }

    if (location.places && Array.isArray(location.places)) {
      // Sort places themselves by index too, then their contacts
      [...location.places].sort(byIndex).forEach((place: any) => {
        if (place.contacts && Array.isArray(place.contacts)) {
          [...place.contacts].sort(byIndex).forEach((contact: Contact) => {
            if (!contactIds.has(contact.id)) {
              contactIds.add(contact.id);
              placeContacts.push({ ...contact, placeName: place.name });
            }
          });
        }
      });
    }

    return { establishmentContacts, placeContacts };
  }, [locationsCache]);

  // Get a specific contact by ID from an entry's location
  const getContactById = useCallback((entry: LFNotifyEntry): Contact | null => {
    if (!entry.contactId) return null;
    const { establishmentContacts, placeContacts } = getContactsForLocation(entry);
    return [...establishmentContacts, ...placeContacts].find((c: Contact) => c.id === entry.contactId) || null;
  }, [getContactsForLocation]);

  // Entry management
  const updateEntry = useCallback((key: string, updates: Partial<LFNotifyEntry>) => {
    setEntries(prev => prev.map(e => e.key === key ? { ...e, ...updates } : e));
  }, []);

  const addEntry = useCallback(() => {
    if (entries.length >= MAX_ENTRIES) return;
    const newKey = Date.now().toString();
    const newEntry: LFNotifyEntry = {
      key: newKey,
      establishmentTypeId: report?.establishmentType?.id,
    };
    // Pre-fetch locations if report has an establishment type
    if (report?.establishmentType?.id) {
      fetchLocationsForType(report.establishmentType.id);
    }
    setEntries(prev => [...prev, newEntry]);
    setExpandedEntryKey(newKey);
  }, [entries.length, report?.establishmentType?.id, fetchLocationsForType]);

  const removeEntry = useCallback((key: string) => {
    if (entries.length <= 1) return;
    const remaining = entries.filter(e => e.key !== key);
    setEntries(remaining);
    // If the removed entry was expanded, expand the last remaining entry
    if (expandedEntryKey === key && remaining.length > 0) {
      setExpandedEntryKey(remaining[remaining.length - 1].key);
    }
  }, [entries, expandedEntryKey]);

  const handleEstablishmentTypeChange = useCallback((entryKey: string, typeId: string) => {
    // Reset location, contact, specific type when type changes
    updateEntry(entryKey, {
      establishmentTypeId: typeId,
      specificType: undefined,
      establishmentLocationId: undefined,
      contactId: undefined,
    });
    fetchLocationsForType(typeId);
  }, [updateEntry, fetchLocationsForType]);

  const handleSpecificTypeChange = useCallback((entryKey: string, specificType: string | undefined) => {
    // Reset location & contact when specific type changes
    updateEntry(entryKey, {
      specificType,
      establishmentLocationId: undefined,
      contactId: undefined,
    });
  }, [updateEntry]);

  const handleLocationChange = useCallback((entryKey: string, locationId: string) => {
    // Find the entry first to get its context
    const entry = entries.find(e => e.key === entryKey);
    if (!entry?.establishmentTypeId) return;

    const locations = locationsCache[entry.establishmentTypeId] || [];
    const location = locations.find((l: EstablishmentLocation) => l.id === locationId);

    // Get contacts for the new location and auto-select the first one
    const tempEntry = { ...entry, establishmentLocationId: locationId };
    const { establishmentContacts, placeContacts } = getContactsForLocation(tempEntry);
    const allContacts = [...establishmentContacts, ...placeContacts];
    const firstContactId = allContacts.length > 0 ? allContacts[0].id : undefined;

    updateEntry(entryKey, {
      establishmentLocationId: locationId,
      contactId: firstContactId
    });
  }, [entries, locationsCache, getContactsForLocation, updateEntry]);

  const handleContactChange = useCallback((entryKey: string, contactId: string) => {
    updateEntry(entryKey, { contactId });
  }, [updateEntry]);

  const handleCommentChange = useCallback((entryKey: string, value: string) => {
    updateEntry(entryKey, { additionalComments: value });
  }, [updateEntry]);

  // Helper: extract all contacts from a location
  const extractAllContacts = useCallback((location: EstablishmentLocation): Contact[] => {
    const contactIds = new Set<string>();
    const allContacts: Contact[] = [];

    if (location.contacts && Array.isArray(location.contacts)) {
      location.contacts.forEach((contact: Contact) => {
        if (!contactIds.has(contact.id)) {
          contactIds.add(contact.id);
          allContacts.push(contact);
        }
      });
    }
    if (location.places && Array.isArray(location.places)) {
      location.places.forEach((place: any) => {
        if (place.contacts && Array.isArray(place.contacts)) {
          place.contacts.forEach((contact: Contact) => {
            if (!contactIds.has(contact.id)) {
              contactIds.add(contact.id);
              allContacts.push(contact);
            }
          });
        }
      });
    }
    return allContacts;
  }, []);

  // Get customer's selected location from LF-marked question answers
  const getCustomerSelectedLocation = useCallback((locations: EstablishmentLocation[]) => {
    if (!report?.establishmentType?.questions) return null;
    const lfQuestions = report.establishmentType.questions.filter((q: any) => q.isLFNotified && q.answers);
    for (const question of lfQuestions) {
      if (question.answers && question.dynamicOptions && Array.isArray(question.dynamicOptions)) {
        const selectedLocation = question.dynamicOptions.find(
          (loc: EstablishmentLocation) => loc.id === question.answers.answer || loc.name === question.answers.answer
        );
        if (selectedLocation) {
          // Resolve specific type from fetched locations if not on the dynamic option
          const fullLocation = locations.find((l: EstablishmentLocation) => l.id === selectedLocation.id);
          return {
            locationId: selectedLocation.id,
            specificType: fullLocation?.specificType || selectedLocation.specificType,
          };
        }
      }
    }
    return null;
  }, [report]);

  // Initialize modal when it opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset state
    setLocationsCache({});
    setLoadingLocations({});
    form.resetFields();

    // Build initial entries from existing notifications if provided
    const existingEntries: LFNotifyEntry[] = (existingNotifications || []).map((n: any, idx: number) => ({
      key: `existing-${idx}`,
      establishmentTypeId: n.establishmentTypeId || report?.establishmentType?.id,
      specificType: n.specificType,
      establishmentLocationId: n.establishmentLocationId,
      contactId: n.contactId,
      additionalComments: n.additionalComments,
      _isExisting: true,
    }));

    // Add a new empty entry for adding more contacts
    const newEntryKey = existingEntries.length > 0 ? `new-${Date.now()}` : '1';
    const newEntry: LFNotifyEntry = {
      key: newEntryKey,
      establishmentTypeId: report?.establishmentType?.id,
    };

    const allEntries = [...existingEntries, newEntry];
    setEntries(allEntries);
    setExpandedEntryKey(newEntryKey);

    // Collect unique establishment type IDs to fetch locations for
    const typeIdsToFetch = new Set<string>();
    if (report?.establishmentType?.id) typeIdsToFetch.add(report.establishmentType.id);
    existingEntries.forEach(e => { if (e.establishmentTypeId) typeIdsToFetch.add(e.establishmentTypeId); });

    // Fetch locations for all needed establishment types
    typeIdsToFetch.forEach(typeId => {
      setLoadingLocations(prev => ({ ...prev, [typeId]: true }));
      api.get<ApiResponse<EstablishmentLocation[]>>(
        `${apiEndpoints.establishmentLocation.getAll}?establishmentTypeId=${typeId}`
      ).then(response => {
        const locations = response.data || [];
        setLocationsCache(prev => ({ ...prev, [typeId]: locations }));

        // Only auto-populate the NEW entry's location (not existing ones) and only if not skipping
        if (typeId === report?.establishmentType?.id && !skipAutoPopulate) {
          let defaultLocationId: string | undefined;
          let defaultSpecificType: string | undefined;

          // Priority 1: Customer's answer to the LF Notification question
          const customerSelection = getCustomerSelectedLocation(locations);
          if (customerSelection?.locationId && locations.some(l => l.id === customerSelection.locationId)) {
            defaultLocationId = customerSelection.locationId;
            defaultSpecificType = customerSelection.specificType;
          }
          // Priority 2: Report's establishment location
          else if (report?.establishmentLocation?.id) {
            defaultLocationId = report.establishmentLocation.id;
            defaultSpecificType = report.establishmentLocation.specificType;
          }
          // Priority 3: First available location
          else if (locations.length > 0) {
            defaultLocationId = locations[0].id;
            defaultSpecificType = locations[0].specificType;
          }

          if (defaultLocationId) {
            const location = locations.find((l: EstablishmentLocation) => l.id === defaultLocationId);
            const allContacts = location ? extractAllContacts(location) : [];

            // Update only the new entry with auto-populated values
            setEntries(prev => prev.map(e => e.key === newEntryKey ? {
              ...e,
              establishmentTypeId: typeId,
              specificType: defaultSpecificType,
              establishmentLocationId: defaultLocationId,
              contactId: allContacts.length > 0 ? allContacts[0].id : undefined,
            } : e));
          }
        }
      }).catch(error => {
        console.error('Failed to fetch initial locations:', error);
      }).finally(() => {
        setLoadingLocations(prev => ({ ...prev, [typeId]: false }));
      });
    });
  }, [isOpen]);

  const handleSubmit = async () => {
    // Only validate and submit NEW entries (not existing ones)
    const newEntries = entries.filter(e => !e._isExisting);
    const invalidEntries = newEntries.filter(e => !e.establishmentTypeId || !e.establishmentLocationId || !e.contactId);
    if (invalidEntries.length > 0) {
      message.error('Please select an establishment type, location, and contact for all entries.');
      return;
    }
    if (newEntries.length === 0) {
      message.error('Please add at least one new contact.');
      return;
    }

    const detailsArray: LFDepartmentDetails[] = newEntries.map(entry => {
      const locations = locationsCache[entry.establishmentTypeId!] || [];
      const location = locations.find((l: EstablishmentLocation) => l.id === entry.establishmentLocationId);
      const contact = getContactById(entry);

      return {
        establishmentTypeId: entry.establishmentTypeId,
        specificType: entry.specificType,
        establishmentLocationId: entry.establishmentLocationId,
        establishmentName: location?.name || '',
        establishmentCode: location?.code || '',
        contactId: entry.contactId,
        isNewContact: false,
        lfDepartmentName: contact?.department || contact?.name || '',
        lfContactPerson: contact?.name || '',
        lfContactEmail: contact?.email || '',
        lfContactPhone: contact?.phone || contact?.mobilePhone || '',
        lfNotes: contact?.notes || '',
        additionalComments: entry.additionalComments || ''
      };
    });

    onSave(detailsArray, notifyCustomer);
  };

  const handleCancel = () => {
    form.resetFields();
    setEntries([{ key: '1' }]);
    setExpandedEntryKey('1');
    setLocationsCache({});
    setLoadingLocations({});
    setNotifyCustomer(true); // reset to default
    onClose();
  };

  const openDrawer = async (entryKey: string) => {
    const entry = entries.find(e => e.key === entryKey);
    if (!entry?.establishmentLocationId) return;

    try {
      const result = await dispatch(fetchSingleEstablishmentLocation(entry.establishmentLocationId)).unwrap();
      setDrawerEntryKey(entryKey);
      setEstablishmentForDrawer(result);
      setEstablishmentDrawerVisible(true);
    } catch (error) {
      console.error('Failed to fetch establishment details:', error);
    }
  };

  // Render a compact contact summary for an entry
  const renderContactSummary = (entry: LFNotifyEntry) => {
    const contact = getContactById(entry);
    if (!contact) return null;

    return (
      <div style={{
        marginTop: 8,
        padding: '8px 12px',
        backgroundColor: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 6,
        fontSize: 12,
        color: '#374151'
      }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'center' }}>
          {(contact.name || contact.department) && (
            <span><strong>{contact.name || contact.department}</strong></span>
          )}
          {contact.lostAndFoundFormLink && (
            <a
              href={contact.lostAndFoundFormLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                backgroundColor: '#2563eb',
                color: '#ffffff',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.35)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'transform 0.1s ease, background-color 0.1s ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#1d4ed8';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#2563eb';
                (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)';
              }}
            >
              <LinkOutlined /> Open L&amp;F Form
            </a>
          )}
          {contact.email && (
            <span style={{ color: '#2563eb' }}>{contact.email}</span>
          )}
          {(contact.phone || contact.mobilePhone) && (
            <span style={{ color: '#6b7280' }}>{contact.phone || contact.mobilePhone}</span>
          )}
          {contact.isPrimary && (
            <span style={{
              backgroundColor: '#dbeafe',
              color: '#1e40af',
              padding: '0px 6px',
              borderRadius: 3,
              fontSize: 10,
              fontWeight: 600
            }}>
              PRIMARY
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render contact select options for an entry
  const renderContactOptions = (entry: LFNotifyEntry) => {
    const { establishmentContacts, placeContacts } = getContactsForLocation(entry);

    return (
      <>
        {establishmentContacts.length > 0 && (
          <Select.OptGroup label="Establishment Contacts">
            {establishmentContacts.map((contact: Contact) => (
              <Select.Option key={contact.id} value={contact.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>{contact.name || contact.department}</span>
                  {contact.isPrimary && (
                    <span style={{
                      backgroundColor: '#dbeafe', color: '#1e40af',
                      padding: '0px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, marginLeft: 8
                    }}>PRIMARY</span>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select.OptGroup>
        )}
        {placeContacts.length > 0 && (
          <Select.OptGroup label="Place Contacts">
            {placeContacts.map((contact: Contact & { placeName?: string }) => (
              <Select.Option key={contact.id} value={contact.id}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>
                    {contact.name || contact.department}
                    {contact.placeName && (
                      <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 4 }}>({contact.placeName})</span>
                    )}
                  </span>
                  {contact.isPrimary && (
                    <span style={{
                      backgroundColor: '#dbeafe', color: '#1e40af',
                      padding: '0px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, marginLeft: 8
                    }}>PRIMARY</span>
                  )}
                </div>
              </Select.Option>
            ))}
          </Select.OptGroup>
        )}
      </>
    );
  };

  // Get display name for a location in an entry
  const getLocationName = useCallback((entry: LFNotifyEntry): string | null => {
    if (!entry.establishmentLocationId || !entry.establishmentTypeId) return null;
    const locations = locationsCache[entry.establishmentTypeId] || [];
    const location = locations.find((l: EstablishmentLocation) => l.id === entry.establishmentLocationId);
    if (!location) return null;
    return [location.name, location.code ? `(${location.code})` : ''].filter(Boolean).join(' ');
  }, [locationsCache]);

  // Get display name for a contact in an entry
  const getContactName = useCallback((entry: LFNotifyEntry): string | null => {
    const contact = getContactById(entry);
    if (!contact) return null;
    return contact.department || contact.name || contact.email || null;
  }, [getContactById]);

  // Render a single entry card
  // Render a read-only card for already-notified entries (expandable)
  const [expandedExistingKey, setExpandedExistingKey] = useState<string>('');

  const renderExistingEntryCard = (entry: LFNotifyEntry, index: number, notification: any) => {
    const isExpanded = expandedExistingKey === entry.key;
    const headerLine = [notification.establishmentName, notification.lfDepartmentName].filter(Boolean).join(' · ');

    return (
      <Card
        key={entry.key}
        size="small"
        style={{ marginBottom: 8, border: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}
        styles={{ body: { padding: '10px 12px' } }}
      >
        {/* Collapsed header — all key info visible */}
        <div
          style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}
          onClick={() => setExpandedExistingKey(isExpanded ? '' : entry.key)}
        >
          {isExpanded
            ? <DownOutlined style={{ fontSize: 10, color: '#6b7280', marginTop: 4, flexShrink: 0 }} />
            : <RightOutlined style={{ fontSize: 10, color: '#6b7280', marginTop: 4, flexShrink: 0 }} />
          }
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#374151', lineHeight: '20px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>{headerLine || 'N/A'}</span>
              {notification.lfContactPerson && (
                <span style={{ color: '#6b7280', fontSize: 12 }}>{notification.lfContactPerson}</span>
              )}
              {notification.lfContactEmail && (
                <span style={{ color: '#2563eb', fontSize: 12 }}>{notification.lfContactEmail}</span>
              )}
              {notification.lfContactPhone && (
                <span style={{ color: '#6b7280', fontSize: 12 }}>{notification.lfContactPhone}</span>
              )}
              {notification.adminComment && (
                <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: 12, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{notification.adminComment}</span>
              )}
            </div>
            {notification.additionalComments && (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginTop: 2 }}>
                {notification.additionalComments}
              </div>
            )}
          </div>
        </div>

        {/* Expanded detail — contact details + notified date */}
        {isExpanded && (
          <div style={{ marginTop: 8, marginLeft: 18, padding: '8px 12px', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, color: '#374151' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
              {notification.establishmentName && (
                <div><span style={{ color: '#9ca3af' }}>Establishment:</span> {notification.establishmentName}</div>
              )}
              {notification.lfDepartmentName && (
                <div><span style={{ color: '#9ca3af' }}>Department:</span> {notification.lfDepartmentName}</div>
              )}
              {notification.lfContactPerson && (
                <div><span style={{ color: '#9ca3af' }}>Contact:</span> {notification.lfContactPerson}</div>
              )}
              {notification.lfContactEmail && (
                <div><span style={{ color: '#9ca3af' }}>Email:</span> {notification.lfContactEmail}</div>
              )}
              {notification.lfContactPhone && (
                <div><span style={{ color: '#9ca3af' }}>Phone:</span> {notification.lfContactPhone}</div>
              )}
              {report?.lfNotifiedAt && (
                <div><span style={{ color: '#9ca3af' }}>Notified:</span> {new Date(report.lfNotifiedAt).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              )}
            </div>
            {notification.additionalComments && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f0f0f0' }}>
                <span style={{ color: '#9ca3af' }}>Comments:</span> {notification.additionalComments}
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const renderEntryCard = (entry: LFNotifyEntry, index: number) => {
    const isExpanded = expandedEntryKey === entry.key;
    const specificTypes = getSpecificTypes(entry.establishmentTypeId);
    const locations = getLocationsForEntry(entry);
    const isLoadingLocs = entry.establishmentTypeId ? loadingLocations[entry.establishmentTypeId] : false;

    const locationName = getLocationName(entry);
    const contactName = getContactName(entry);

    return (
      <Card
        key={entry.key}
        size="small"
        style={{ marginBottom: 12, border: '1px solid #d9d9d9' }}
        styles={{ body: { padding: '12px 16px' } }}
      >
        {/* Header - always visible, clickable to expand/collapse */}
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setExpandedEntryKey(isExpanded ? '' : entry.key)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {isExpanded ? (
              <DownOutlined style={{ fontSize: 10, color: '#6b7280' }} />
            ) : (
              <RightOutlined style={{ fontSize: 10, color: '#6b7280' }} />
            )}
            <span style={{ fontWeight: 600, fontSize: 13, color: '#1f2937', flexShrink: 0 }}>
              Entry {index + 1}
            </span>
            {!isExpanded && (locationName || contactName) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden' }}>
                <span style={{ color: '#d9d9d9' }}>|</span>
                {locationName && (
                  <span style={{
                    fontSize: 12,
                    color: '#374151',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {locationName}
                  </span>
                )}
                {locationName && contactName && (
                  <span style={{ color: '#d9d9d9', flexShrink: 0 }}>&middot;</span>
                )}
                {contactName && (
                  <span style={{
                    fontSize: 12,
                    color: '#6b7280',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {contactName}
                  </span>
                )}
              </div>
            )}
          </div>
          {entries.length > 1 && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                removeEntry(entry.key);
              }}
            />
          )}
        </div>

        {/* Body - only when expanded */}
        {isExpanded && (
          <div style={{ marginTop: 12 }}>
            {/* Establishment Type + Specific Type */}
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={specificTypes.length > 0 ? 12 : 24}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' }}>
                    <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                    Establishment Type
                  </label>
                </div>
                <Select
                  placeholder="Select establishment type"
                  value={entry.establishmentTypeId}
                  onChange={(val: string) => handleEstablishmentTypeChange(entry.key, val)}
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                >
                  {establishmentTypes.map((type: EstablishmentType) => (
                    <Select.Option key={type.id} value={type.id}>{type.name}</Select.Option>
                  ))}
                </Select>
              </Col>

              {specificTypes.length > 0 && (
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <label style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' }}>
                      Specific Type
                    </label>
                  </div>
                  <Select
                    placeholder="Select specific type (optional)"
                    value={entry.specificType}
                    onChange={(val: string | undefined) => handleSpecificTypeChange(entry.key, val)}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {specificTypes.map((type: string) => (
                      <Select.Option key={type} value={type}>{type}</Select.Option>
                    ))}
                  </Select>
                </Col>
              )}
            </Row>

            {/* Establishment Location + Contact */}
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ marginBottom: 4 }}>
                  <label style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' }}>
                    <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                    Establishment Location
                  </label>
                </div>
                <Select
                  placeholder="Select establishment location"
                  value={entry.establishmentLocationId}
                  onChange={(val: string) => handleLocationChange(entry.key, val)}
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                  loading={isLoadingLocs}
                  disabled={!entry.establishmentTypeId}
                >
                  {locations.map((el: EstablishmentLocation) => {
                    const displayName = [el.name, el.code ? `(${el.code})` : ''].filter(Boolean).join(' ');
                    return (
                      <Select.Option key={el.id} value={el.id}>{displayName}</Select.Option>
                    );
                  })}
                </Select>
              </Col>

              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <label style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' }}>
                    <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                    L&F Contact
                  </label>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => openDrawer(entry.key)}
                    disabled={!entry.establishmentLocationId}
                    style={{ padding: '0 4px', height: 'auto', fontSize: 12 }}
                  >
                    + Add New
                  </Button>
                </div>
                <Select
                  placeholder="Select L&F contact"
                  value={entry.contactId}
                  onChange={(val: string) => handleContactChange(entry.key, val)}
                  disabled={!entry.establishmentLocationId}
                  showSearch
                  optionFilterProp="children"
                  style={{ width: '100%' }}
                >
                  {renderContactOptions(entry)}
                </Select>
              </Col>
            </Row>

            {renderContactSummary(entry)}

            {/* Per-entry comments */}
            <div style={{ marginTop: 12, marginBottom: 20 }}>
              <div style={{ marginBottom: 4 }}>
                <label style={{ fontSize: 13, color: 'rgba(0, 0, 0, 0.88)' }}>
                  Comments (Optional)
                </label>
              </div>
              <Input.TextArea
                rows={2}
                placeholder="Enter any comments or instructions for this entry..."
                maxLength={500}
                showCount
                value={entry.additionalComments || ''}
                onChange={(e) => handleCommentChange(entry.key, e.target.value)}
              />
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <>
      <Modal
        title="Lost & Found Department Details"
        open={isOpen}
        onCancel={handleCancel}
        maskClosable={false}
        footer={
          // Custom footer: "Notify customer via email" checkbox left-aligned, action buttons
          // right-aligned. Button label toggles so admins always see exactly what the click
          // will do — notifying "Confirm & Notify" (default) or silent "Confirm".
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <Checkbox
              checked={notifyCustomer}
              onChange={(e) => setNotifyCustomer(e.target.checked)}
              disabled={loading}
            >
              Notify customer via email
            </Checkbox>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button key="cancel" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
                {confirmButtonText || (notifyCustomer ? 'Confirm & Notify' : 'Confirm')}
              </Button>
            </div>
          </div>
        }
        width={1000}
      >
        <p style={{ marginBottom: 16, color: '#6b7280' }}>
          Select the establishment and Lost & Found contacts to notify. You can add up to {MAX_ENTRIES} entries.
        </p>

        <Form form={form} layout="vertical" autoComplete="off">
          {/* Existing notified contacts (read-only) */}
          {existingNotifications && existingNotifications.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Already Notified
              </div>
              {entries.filter(e => e._isExisting).map((entry, index) =>
                renderExistingEntryCard(entry, index, existingNotifications[index])
              )}
            </div>
          )}

          {/* New entries */}
          <div style={{ marginBottom: 16 }}>
            {existingNotifications && existingNotifications.length > 0 && (
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                New Contact
              </div>
            )}
            {entries.filter(e => !e._isExisting).map((entry, index) => renderEntryCard(entry, index))}

            {entries.filter(e => !e._isExisting).length < MAX_ENTRIES && (
              <Button
                type="dashed"
                onClick={addEntry}
                block
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
              >
                Add Another Entry ({entries.length}/{MAX_ENTRIES})
              </Button>
            )}
          </div>
        </Form>
      </Modal>

      {/* Address Book Drawer */}
      <PlacesContactsDrawer
        visible={establishmentDrawerVisible}
        onClose={async () => {
          setEstablishmentDrawerVisible(false);
          setEstablishmentForDrawer(null);
          // Refresh establishment location data to get newly created contacts
          const activeEntry = entries.find(e => e.key === drawerEntryKey);
          if (activeEntry?.establishmentLocationId && activeEntry?.establishmentTypeId) {
            try {
              const result = await dispatch(fetchSingleEstablishmentLocation(activeEntry.establishmentLocationId)).unwrap();
              // Update the cached locations with refreshed data
              setLocationsCache(prev => {
                const typeId = activeEntry.establishmentTypeId!;
                const existingLocations = prev[typeId] || [];
                const updatedLocations = existingLocations.map(loc =>
                  loc.id === result.id ? result : loc
                );
                return { ...prev, [typeId]: updatedLocations };
              });
            } catch (error) {
              console.error('Failed to refresh location:', error);
            }
          }
          setDrawerEntryKey(null);
        }}
        establishment={establishmentForDrawer}
      />
    </>
  );
};

export default LFNotifiedModal;
